import { DocumentNode } from 'graphql';
import { useModal } from 'react-modal-hook';
import { useTranslation } from 'react-i18next';

import { GetParticipantFieldImportDetailsQuery as GetParticipantFieldImportDetails } from '__generated__/graphql';
import { useNotifier } from '../../../common/Notifications/NotificationProvider';
import GetParticipantFieldImportDetailsQuery from './GetParticipantFieldImportDetailsQuery';
import SendImportConfirmationsMutation from './SendImportConfirmationsMutation';
import UI from '../../../common/UI';
import useLocale from '../../../common/useLocale';
import useMutation from '../../../api/useMutation';

type ParticipantFieldImport = GetParticipantFieldImportDetails['project']['participant_field_import'];

export interface SendImportConfirmationMailFormProps {
  participantFieldImport: ParticipantFieldImport;
  onSuccess?: () => void;
  onCancel?: () => void;
  refetchQueries?: DocumentNode[];
}

export const SendImportConfirmationMailForm = ({
  participantFieldImport,
  onSuccess,
  onCancel,
  refetchQueries = [GetParticipantFieldImportDetailsQuery],
}: SendImportConfirmationMailFormProps) => {
  const { t } = useTranslation();
  const { formatInteger } = useLocale();
  const notifier = useNotifier();

  const [sendConfirmation, { loading: sending }] = useMutation(
    SendImportConfirmationsMutation,
    {
      onCompleted: () => {
        onSuccess?.();
        notifier.success(t('notifications.confirmation_sent'));
      },
      refetchQueries,
      awaitRefetchQueries: true,
    },
  );

  return (
    <UI.Form onSubmit={() => sendConfirmation({ variables: { id: participantFieldImport.id } })}>
      <UI.FormGrid>
        <UI.H4>
          {t('send_confirmation_mails')}
        </UI.H4>
        <UI.HR />
        <UI.InputGroup>
          <UI.InputLabel>{t('registrations')}</UI.InputLabel>
          {formatInteger(participantFieldImport.length)}
        </UI.InputGroup>
        <UI.InputGroup>
          <UI.InputLabel>{t('contacts')}</UI.InputLabel>
          {formatInteger(participantFieldImport.confirmation_mail_recipients_count)}
        </UI.InputGroup>
        <UI.HR />
        <UI.Div sc={{ textAlign: 'right' }}>
          {onCancel && (
            <UI.Button onClick={() => onCancel()} disabled={sending}>
              {t('cancel')}
            </UI.Button>
          )}
          {' '}
          <UI.Button
            type="submit"
            sc={{ brand: 'secondary' }}
            disabled={sending}
          >
            {t('confirm')}
          </UI.Button>
        </UI.Div>
      </UI.FormGrid>
    </UI.Form>
  );
};
interface UseSendImportConfirmationMailModalProps {
  participantFieldImport: ParticipantFieldImport;
}

const useSendImportConfirmationMailModal = ({ participantFieldImport }: UseSendImportConfirmationMailModalProps) => {
  const [openModal, closeModal] = useModal(
    () => (
      <UI.Modal close={closeModal}>
        <SendImportConfirmationMailForm
          participantFieldImport={participantFieldImport}
          onSuccess={closeModal}
          onCancel={closeModal}
        />
      </UI.Modal>
    ),
    [participantFieldImport],
  );

  return [openModal, closeModal];
};

export default useSendImportConfirmationMailModal;
